import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, Trash2, Bell, MapPin, KeyRound, Compass } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { InstallShareCard } from "@/components/InstallShare";
import SupportWidget from "@/components/SupportWidget";
import { LifeBuoy } from "lucide-react";
import { BELIEF_OPTIONS } from "@/lib/gatewayContent";

const Settings = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: user?.name || "", age: user?.age || "", ethnicity: user?.ethnicity || "",
    cultural_background: user?.cultural_background || "", country: user?.country || "",
    city: user?.city || "", state: user?.state || "",
    emergency_contact_name: user?.emergency_contact_name || "", emergency_contact_phone: user?.emergency_contact_phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [reminder, setReminder] = useState({ enabled: user?.reminder_enabled ?? false, time: user?.reminder_time || "20:00" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [belief, setBelief] = useState(user?.belief_filter || "jungian");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const saveBelief = async (v) => {
    setBelief(v);
    try {
      const { data } = await api.put("/auth/profile", { belief_filter: v });
      setUser(data);
      toast.success("Your wisdom lens is saved.");
    } catch { toast.error("Couldn't save preference."); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Your password has been updated.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setPwSaving(false);
    }
  };

  const saveReminder = async (next) => {
    setReminder(next);
    try {
      await api.put("/reminders", next);
      toast.success("Reminder preferences saved.");
    } catch {
      toast.error("Couldn't save reminder.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { ...f, age: parseInt(f.age, 10) });
      setUser(data);
      toast.success("Your profile has been updated.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteAll = async () => {
    try {
      await api.delete("/account");
      toast.success("All your data has been permanently deleted.");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const wipeCanvas = async () => {
    try {
      const { data } = await api.delete("/journal/all");
      toast.success(`Canvas wiped — ${data.deleted} entr${data.deleted === 1 ? "y" : "ies"} permanently deleted.`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const input = "rounded-2xl bg-linen border-linen-3 h-12";

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink">Settings</h1>
        <p className="text-ink-2 mt-2">Your space, your control.</p>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8 space-y-5">
        <h2 className="font-serif text-2xl text-ink">Profile & culture</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label className="text-ink-2">Name</Label><Input value={f.name} onChange={set("name")} className={input} data-testid="set-name" /></div>
          <div className="space-y-2"><Label className="text-ink-2">Age</Label><Input type="number" value={f.age} onChange={set("age")} className={input} data-testid="set-age" /></div>
          <div className="space-y-2"><Label className="text-ink-2">Ethnicity</Label><Input value={f.ethnicity} onChange={set("ethnicity")} className={input} data-testid="set-ethnicity" /></div>
          <div className="space-y-2"><Label className="text-ink-2">Country / region</Label><Input value={f.country} onChange={set("country")} className={input} data-testid="set-country" /></div>
        </div>
        <div className="space-y-2"><Label className="text-ink-2">Cultural background</Label><Textarea value={f.cultural_background} onChange={set("cultural_background")} className="rounded-2xl bg-linen border-linen-3 min-h-[80px]" data-testid="set-culture" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label className="text-ink-2">Emergency contact name</Label><Input value={f.emergency_contact_name} onChange={set("emergency_contact_name")} className={input} data-testid="set-ec-name" /></div>
          <div className="space-y-2"><Label className="text-ink-2">Emergency contact phone</Label><Input value={f.emergency_contact_phone} onChange={set("emergency_contact_phone")} className={input} data-testid="set-ec-phone" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-linen px-6" data-testid="set-save">{saving ? "Saving…" : "Save changes"}</Button>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8 space-y-5" data-testid="location-card">
        <h2 className="font-serif text-2xl text-ink flex items-center gap-2"><MapPin className="w-5 h-5 text-brand" /> Local location</h2>
        <p className="text-sm text-ink-2 -mt-1">Aurea uses your location to show the closest <span className="font-medium">helpline numbers</span> and the most relevant local <span className="font-medium">resources</span> (shelters, food banks, meetings, and crisis support).</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2"><Label className="text-ink-2">Country</Label><Input value={f.country} onChange={set("country")} placeholder="United States" className={input} data-testid="loc-country" /></div>
          <div className="space-y-2"><Label className="text-ink-2">State / region</Label><Input value={f.state} onChange={set("state")} placeholder="Arizona" className={input} data-testid="loc-state" /></div>
          <div className="space-y-2"><Label className="text-ink-2">City</Label><Input value={f.city} onChange={set("city")} placeholder="Phoenix" className={input} data-testid="loc-city" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-linen px-6" data-testid="loc-save">{saving ? "Saving…" : "Save location"}</Button>
      </div>

      <InstallShareCard />

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8 space-y-4" data-testid="belief-card">
        <h2 className="font-serif text-2xl text-ink flex items-center gap-2"><Compass className="w-5 h-5 text-brand" /> Wisdom lens</h2>
        <p className="text-sm text-ink-2 -mt-1">Choose the voice for the quotes and gentle guidance Aurea offers you.</p>
        <div className="flex flex-wrap gap-2">
          {BELIEF_OPTIONS.map((o) => (
            <button key={o.v} onClick={() => saveBelief(o.v)} data-testid={`belief-${o.v}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${belief === o.v ? "bg-brand text-linen border-brand" : "bg-linen border-linen-3 text-ink-2 hover:bg-linen-2"}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8 space-y-5" data-testid="password-card">
        <h2 className="font-serif text-2xl text-ink flex items-center gap-2"><KeyRound className="w-5 h-5 text-brand" /> Change password</h2>
        <div className="space-y-4 max-w-md">
          <div className="space-y-2"><Label className="text-ink-2">Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={input} data-testid="pw-current" /></div>
          <div className="space-y-2"><Label className="text-ink-2">New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={input} data-testid="pw-new" /></div>
          <div className="space-y-2"><Label className="text-ink-2">Confirm new password</Label><Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={input} data-testid="pw-confirm" /></div>
          <Button onClick={changePassword} disabled={pwSaving} className="rounded-full bg-brand hover:bg-brand-hover text-linen px-6" data-testid="pw-save">{pwSaving ? "Updating…" : "Update password"}</Button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8 space-y-5" data-testid="reminder-card">
        <h2 className="font-serif text-2xl text-ink flex items-center gap-2"><Bell className="w-5 h-5 text-brand" /> Daily check-in reminder</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-ink">Gentle daily nudge</p>
            <p className="text-sm text-ink-mute">A soft reminder on your dashboard to pause and check in.</p>
          </div>
          <Switch checked={reminder.enabled} onCheckedChange={(v) => saveReminder({ ...reminder, enabled: v })} data-testid="reminder-toggle" />
        </div>
        {reminder.enabled && (
          <div className="space-y-2 max-w-xs">
            <Label className="text-ink-2">Preferred time</Label>
            <Input type="time" value={reminder.time} onChange={(e) => saveReminder({ ...reminder, time: e.target.value })} className="rounded-2xl bg-linen border-linen-3 h-12" data-testid="reminder-time" />
          </div>
        )}
      </div>

      <div className="bg-calm rounded-3xl p-6 sm:p-8">
        <h2 className="font-serif text-2xl text-calm-text flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Your privacy</h2>
        <p className="text-sm text-calm-text/80 mt-2 leading-relaxed">
          Aurea is built to protect your peace of mind. Your reflections stay private. We follow privacy best-practices,
          though this is a wellbeing tool — not a certified clinical or HIPAA-covered service. In an emergency, always
          contact local emergency services.
        </p>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8" data-testid="settings-support-card">
        <h2 className="font-serif text-2xl text-ink flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-brand" /> Report an Issue / Send Feedback</h2>
        <p className="text-sm text-ink-2 mt-2 leading-relaxed">Found a bug, hit a login snag, or have an idea to make Aurea gentler? Tell us — it reaches the team directly.</p>
        <div className="mt-4"><SupportWidget tone="light" /></div>
      </div>

      <div className="bg-white rounded-3xl border border-linen-3 p-6 sm:p-8" data-testid="wipe-canvas-card">
        <h2 className="font-serif text-2xl text-ink">Wipe Canvas History</h2>
        <p className="text-sm text-ink-2 mt-2 leading-relaxed">Permanently erase every Mind Dump and journal entry you've written — instantly and forever. Your account, moods and settings stay intact. Nothing can be recovered.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="rounded-full border-accent text-accent hover:bg-accent hover:text-linen mt-4" data-testid="wipe-canvas-trigger"><Trash2 className="w-4 h-4 mr-2" /> Wipe Canvas History</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-linen rounded-3xl" data-testid="wipe-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl text-ink">Wipe all your writing?</AlertDialogTitle>
              <AlertDialogDescription className="text-ink-2">This permanently deletes every Mind Dump and journal entry from the database. There is no recovery.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full" data-testid="wipe-cancel">Keep my writing</AlertDialogCancel>
              <AlertDialogAction onClick={wipeCanvas} className="rounded-full bg-accent hover:bg-accent-hover text-linen" data-testid="wipe-confirm">Yes, wipe it all</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="bg-white rounded-3xl border border-warmsoft p-6 sm:p-8">
        <h2 className="font-serif text-2xl text-warmsoft-text">Delete everything</h2>
        <p className="text-sm text-ink-2 mt-2 leading-relaxed">Permanently erase your account, journals, conversations, moods and appointments. This cannot be undone.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="rounded-full bg-accent hover:bg-accent-hover text-linen mt-4" data-testid="delete-account-trigger"><Trash2 className="w-4 h-4 mr-2" /> Delete my data</Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-linen rounded-3xl" data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl text-ink">Delete all your data?</AlertDialogTitle>
              <AlertDialogDescription className="text-ink-2">This will permanently remove everything and sign you out. There is no recovery.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full" data-testid="delete-cancel">Keep my data</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAll} className="rounded-full bg-accent hover:bg-accent-hover text-linen" data-testid="delete-confirm">Yes, delete forever</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default Settings;
